import { useMemo } from 'react'
import { Modal, ModalProps, Form, Select, Input, DatePicker } from 'antd'
import moment from 'moment'

import {
  ZoneSuspensionReason,
  ZoneScheduledSuspendMutationVariables,
} from '~/gql/generated/graphql'
import { capitalize } from '~/utils'
import { RangePickerProps } from 'antd/lib/date-picker'

export type ScheduledSuspendModalProps = ModalProps & {
  // Number of bays affected, used in title
  count?: number
  onConfirm: (
    info: Pick<
      ZoneScheduledSuspendMutationVariables,
      'end_time' | 'start_time' | 'reason' | 'notes'
    >,
  ) => void
}

const { RangePicker } = DatePicker

// Disallow dates in the past
const disabledDate: RangePickerProps['disabledDate'] = current => {
  return current < moment().startOf('day')
}

// Collect a time-range, suspension reason and notes
export const ScheduledSuspendModal = ({
  open,
  onCancel,
  onConfirm,
  count,
}: ScheduledSuspendModalProps) => {
  const [form] = Form.useForm()

  const reasonOptions = useMemo(
    () =>
      Object.entries(ZoneSuspensionReason)
        .filter(([key]) => key !== 'unknown')
        .map(([key, value]) => ({
          value,
          label: capitalize(key.replace(/_/g, ' ')),
        })),
    [],
  )

  const reasonChosen = Form.useWatch('reason', form)
  const timeRangeChosen = Form.useWatch('time_range', form)

  const handleOk = async () => {
    const { time_range, reason, notes } = await form.validateFields()
    const [start_time, end_time] = time_range
    onConfirm({
      start_time: start_time
        ? start_time.utc().format('YYYY-MM-DDTHH:mm:ssZ')
        : '',
      end_time: end_time ? end_time.utc().format('YYYY-MM-DDTHH:mm:ssZ') : '',
      reason,
      notes: notes ?? '',
    })
    form.resetFields()
  }

  const handleCancel = (e: React.MouseEvent<HTMLElement>) => {
    onCancel?.(e)
    form.resetFields()
  }

  return (
    <Modal
      // 1050? lets modal appear above "actions" dropdown
      zIndex={1050}
      open={open}
      title={`Suspend ${count ?? ''} bay${count === 1 ? '' : 's'}?`}
      okButtonProps={{
        disabled: !(
          reasonChosen &&
          timeRangeChosen &&
          timeRangeChosen.length === 2
        ),
      }}
      onOk={handleOk}
      onCancel={handleCancel}
      destroyOnClose
    >
      <Form layout="vertical" form={form} name="suspendModalForm">
        <Form.Item
          name="time_range"
          label="Suspend Between"
          rules={[
            { required: true, message: 'Please choose a start and end time' },
          ]}
          tooltip="The start and end times this bay should be suspended"
        >
          <RangePicker
            showTime={{
              format: 'HH:mm',
              defaultValue: [moment('00:00:00', 'HH:mm:ss')],
            }}
            format="YYYY-MM-DD HH:mm"
            minuteStep={15}
            disabledDate={disabledDate}
          />
        </Form.Item>
        <Form.Item
          name="reason"
          label="Reason"
          rules={[{ required: true, message: 'Please choose a reason' }]}
          tooltip="The reason these bays are being suspended"
        >
          <Select options={reasonOptions} />
        </Form.Item>

        <Form.Item
          name="notes"
          label="Notes"
          tooltip="Additional info about this suspension"
        >
          <Input.TextArea />
        </Form.Item>
      </Form>
    </Modal>
  )
}
